import { X } from "lucide-react";
import type { GRCActiveFilters, GRCReportGridResponse } from "@/types/grc";

interface GRCFilterPanelProps {
  data: GRCReportGridResponse | undefined;
  filters: GRCActiveFilters;
  onFiltersChange: (f: GRCActiveFilters) => void;
  onClose: () => void;
}

/**
 * Right-side filter flyout: checkboxes for Event Type and Event Status.
 * Mirrors Blazor GRCReport.ApplyFilters.
 */
export function GRCFilterPanel({
  data,
  filters,
  onFiltersChange,
  onClose
}: GRCFilterPanelProps) {
  const allEventTypes = [
    ...new Set(data?.programDetails.map((p) => p.eventType).filter(Boolean) ?? [])
  ].sort((a, b) => a.localeCompare(b));
  const allStatuses = [
    ...new Set(data?.programDetails.map((p) => p.eventStatus).filter(Boolean) ?? [])
  ].sort((a, b) => a.localeCompare(b));

  function toggleEventType(type: string) {
    onFiltersChange({
      ...filters,
      eventTypes: filters.eventTypes.includes(type)
        ? filters.eventTypes.filter((t) => t !== type)
        : [...filters.eventTypes, type]
    });
  }

  function toggleStatus(status: string) {
    onFiltersChange({
      ...filters,
      statuses: filters.statuses.includes(status)
        ? filters.statuses.filter((s) => s !== status)
        : [...filters.statuses, status]
    });
  }

  function clearAll() {
    onFiltersChange({ eventTypes: [], statuses: [] });
  }

  function applyAndClose() {
    onClose();
  }

  return (
    <div
      className="fixed inset-0 z-50 flex bg-black/30"
      onClick={onClose}
      aria-hidden="true"
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="grc-filter-title"
        className="ml-auto flex w-[30vw] min-w-[280px] flex-col border-l border-slate-200 bg-white shadow-xl"
        onClick={(e) => e.stopPropagation()}
        onKeyDown={(e) => { if (e.key === "Escape") onClose(); }}
      >
        {/* Header */}
        <div className="flex items-center justify-between bg-black px-4 py-3">
          <h2 id="grc-filter-title" className="text-sm font-semibold text-white">Filters</h2>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={clearAll}
              className="text-xs text-slate-300 hover:text-white underline"
            >
              Clear all
            </button>
            <button type="button" onClick={onClose} aria-label="Close filters">
              <X className="h-4 w-4 text-slate-300 hover:text-white" />
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto px-4 py-4 space-y-6">
          {/* Event Type */}
          <fieldset>
            <legend className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-600">
              Event Type
            </legend>
            <div className="space-y-2">
              {allEventTypes.map((type) => (
                <label key={type} className="flex cursor-pointer items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    className="h-4 w-4 rounded-none border-slate-300 text-slate-900"
                    checked={filters.eventTypes.includes(type)}
                    onChange={() => toggleEventType(type)}
                  />
                  {type}
                </label>
              ))}
              {allEventTypes.length === 0 && (
                <p className="text-xs text-slate-400">No data loaded</p>
              )}
            </div>
          </fieldset>

          {/* Status */}
          <fieldset>
            <legend className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-600">
              Status
            </legend>
            <div className="space-y-2">
              {allStatuses.map((status) => (
                <label key={status} className="flex cursor-pointer items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    className="h-4 w-4 rounded-none border-slate-300 text-slate-900"
                    checked={filters.statuses.includes(status)}
                    onChange={() => toggleStatus(status)}
                  />
                  {status}
                </label>
              ))}
              {allStatuses.length === 0 && (
                <p className="text-xs text-slate-400">No data loaded</p>
              )}
            </div>
          </fieldset>
        </div>

        <div className="border-t border-slate-200 px-4 py-3">
          <button
            type="button"
            onClick={applyAndClose}
            className="w-full border border-slate-900 bg-slate-900 py-2 text-sm font-medium text-white hover:bg-slate-800"
          >
            Apply
          </button>
        </div>
      </div>
    </div>
  );
}
